/**
 * Drag-and-drop kanban board. Owns the DndContext and the move logic.
 *
 * Each pipeline row from `usePipeline()` renders as a KanbanColumn; each
 * `pipeline_properties` row belonging to that pipeline renders as a
 * KanbanCard inside it.
 *
 * On drop:
 *   1. Resolve the source pipeline (where the card was) and the target
 *      pipeline (where it landed) — both from the dnd-kit event.
 *   2. Delegate to `usePipelineMutations().moveDeal`, which enforces the
 *      required-checklist gate and only persists on success.
 *   3. The gate failure path surfaces a toast inside the mutation.
 */
import { useCallback, useMemo, useState } from "react";
import { toast } from "sonner";
import {
  DndContext,
  DragOverlay,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
  type DragEndEvent,
  type DragStartEvent,
} from "@dnd-kit/core";

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { usePipeline } from "@/hooks/use-pipeline";
import { usePipelineMutations } from "@/hooks/use-pipeline-mutations";
import { useRefreshListingStatus } from "@/hooks/use-refresh-listing-status";
import {
  LISTING_STATUS_LABELS,
  blocksAcquisition,
  decideAcquisitionGate,
  resolveListingStatus,
} from "@/lib/listing-status";
import type { PipelineRow, Strategy } from "@/types/pipeline";

import { ClosedWonDialog, type ClosedWonDeal } from "./ClosedWonDialog";
import { KanbanCard } from "./KanbanCard";
import { KanbanColumn } from "./KanbanColumn";

interface KanbanBoardProps {
  /** Open the property detail view for the clicked card. */
  onOpenDeal?: (pipelinePropertyId: string) => void;
  /** Add-deal button per column (Phase 2: opens the NewDealDrawer). */
  onAddDeal?: (pipelineId: string) => void;
}

interface CardRow {
  id: string;
  pipeline_id?: string | null;
  strategy?: Strategy | null;
  property_data?: Record<string, unknown>;
  purchase_price?: number | null;
  notes?: string | null;
  /** Pre-acquisition market status from the feed — drives the card's pill. */
  listing_status?: string | null;
}

/**
 * `property_data` is stored as the `MockProperty` shape coming out of the
 * Find page (address/city/state/zip/imageUrl/bedrooms/bathrooms/sqft/price).
 * Falls back to the legacy HTML-mockup field names (addr1/addr2/img/bd/ba)
 * so any older rows still render.
 *
 * The status pill reads `listing_status` (the column) rather than the status
 * buried in `property_data`, which was a snapshot taken at add-to-pipeline time
 * and never refreshed.
 */
function toCardData(deal: CardRow) {
  const data = (deal.property_data ?? {}) as Record<string, unknown>;
  const city = (data.city as string | undefined) ?? "";
  const state = (data.state as string | undefined) ?? "";
  const zip = (data.zip as string | undefined) ?? "";
  const addr2 =
    city || state || zip
      ? `${city}${state ? `, ${state}` : ""}${zip ? ` ${zip}` : ""}`
      : ((data.addr2 as string | undefined) ?? null);
  return {
    id: deal.id,
    strategy: deal.strategy ?? null,
    listingStatus: resolveListingStatus(deal),
    addr1:
      (data.address as string | undefined) ??
      (data.addr1 as string | undefined) ??
      null,
    addr2,
    listedPrice:
      (data.price as string | number | undefined) ??
      (data.listedPrice as string | number | undefined) ??
      null,
    purchasePrice: deal.purchase_price ?? null,
    bd:
      (data.bedrooms as number | undefined) ??
      (data.bd as number | undefined) ??
      null,
    ba:
      (data.bathrooms as number | undefined) ??
      (data.ba as number | undefined) ??
      null,
    sqft: (data.sqft as string | number | undefined) ?? null,
    imgUrl:
      (data.imageUrl as string | undefined) ??
      (data.img as string | undefined) ??
      null,
  };
}

export function KanbanBoard({ onOpenDeal, onAddDeal }: KanbanBoardProps) {
  const { pipelines, savedProperties, getPropertiesByPipeline, removeDeal } =
    usePipeline();
  const { moveDealMutation } = usePipelineMutations();
  // Own instance, so its `isPending` is independent of the page-header sweep.
  const refreshListingStatus = useRefreshListingStatus();
  const [activeId, setActiveId] = useState<string | null>(null);
  /** Deal whose market status is being confirmed after a Closed-Won drop. */
  const [checkingDealId, setCheckingDealId] = useState<string | null>(null);
  /**
   * Deal queued for deletion, pending confirmation. Held at board level (like
   * `closedWonTarget`) rather than per card, so one dialog serves the whole
   * board and no dialog lives inside a draggable.
   */
  const [removeTarget, setRemoveTarget] = useState<{
    id: string;
    address: string;
  } | null>(null);
  const [isRemoving, setIsRemoving] = useState(false);
  // A move into the Closed-Won stage routes through the acquisition modal
  // instead of moving the card straight away.
  const [closedWonTarget, setClosedWonTarget] = useState<{
    dealId: string;
    fromPipeline: PipelineRow;
    toPipeline: PipelineRow;
  } | null>(null);

  /**
   * Move a deal to `to`. Closed-Won is special: open the acquisition modal
   * (ownership / owned date / purchase price / loan) and let it perform the
   * move on confirm. Every other stage moves immediately.
   *
   * Dropping onto Closed-Won means "I bought this", so the drop re-checks the
   * property feed live before offering the modal — the stored status may be days
   * old, or never have been refreshed at all. If the feed says another buyer
   * closed on it, the move is refused outright.
   *
   * Three layers guard this, cheapest first: the stored value here, the live
   * feed here, then a DB re-read inside `moveToClosedWonMutation` (which sees
   * whatever the refresh just wrote). Only the last one is authoritative.
   */
  const requestMove = useCallback(
    async (dealId: string, from: PipelineRow, to: PipelineRow) => {
      if (to.kind !== "closed_won") {
        moveDealMutation.mutate({
          pipelinePropertyId: dealId,
          fromPipeline: from,
          toPipeline: to,
        });
        return;
      }

      const row = (savedProperties as unknown as CardRow[]).find(
        (p) => p.id === dealId,
      );
      const stored = resolveListingStatus(row ?? {});

      // Already known-sold: refuse without spending an API call.
      if (blocksAcquisition(stored)) {
        toast.error(
          `Can't close — the listing is ${LISTING_STATUS_LABELS[stored].toLowerCase()}. ` +
            "Another buyer already closed on this property.",
        );
        return;
      }

      // One check at a time, so a flurry of drops can't stack API calls.
      if (checkingDealId) {
        toast.info("Still checking market status — try again in a moment.");
        return;
      }

      setCheckingDealId(dealId);
      const toastId = toast.loading("Checking market status…");
      const result = await refreshListingStatus
        .mutateAsync({ pipelinePropertyIds: [dealId], silent: true })
        .catch(() => null);
      toast.dismiss(toastId);
      setCheckingDealId(null);

      // `to` is undefined when the feed couldn't be read; the gate falls back to
      // the stored status rather than blocking on an API hiccup.
      const gate = decideAcquisitionGate(
        stored,
        result?.outcomes.find((o) => o.id === dealId)?.to,
      );

      if (!gate.allow) {
        toast.error(
          `Can't close — the listing is now ${LISTING_STATUS_LABELS[
            gate.reason
          ].toLowerCase()}. Another buyer closed on this property.`,
        );
        return;
      }

      // Under contract with someone else isn't fatal, but the user should know
      // before they record an acquisition.
      if (gate.warn) {
        toast.warning(
          `Heads up — the listing is now ${LISTING_STATUS_LABELS[gate.warn].toLowerCase()}.`,
        );
      }

      setClosedWonTarget({ dealId, fromPipeline: from, toPipeline: to });
    },
    [checkingDealId, moveDealMutation, refreshListingStatus, savedProperties],
  );

  /** Open the confirm dialog for a card's remove button. */
  const requestRemove = useCallback(
    (dealId: string) => {
      const row = (savedProperties as unknown as CardRow[]).find(
        (p) => p.id === dealId,
      );
      const address =
        (row?.property_data?.address as string | undefined) ?? "this deal";
      setRemoveTarget({ id: dealId, address });
    },
    [savedProperties],
  );

  const confirmRemove = useCallback(async () => {
    if (!removeTarget) return;
    setIsRemoving(true);
    // removeDeal reports its own success/failure toast; the dialog closes either
    // way, since a failure leaves the card in place for the user to see.
    await removeDeal(removeTarget.id);
    setIsRemoving(false);
    setRemoveTarget(null);
  }, [removeDeal, removeTarget]);

  // Require small drag distance before activating — lets clicks (open detail)
  // through without firing a drag.
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
  );

  /** Find the pipeline that currently owns a card. */
  const pipelineFor = useMemo(() => {
    return (dealId: string): PipelineRow | null => {
      const row = (savedProperties as unknown as CardRow[]).find(
        (p) => p.id === dealId,
      );
      if (!row?.pipeline_id) return null;
      return pipelines.find((p) => p.id === row.pipeline_id) ?? null;
    };
  }, [pipelines, savedProperties]);

  function handleDragStart(event: DragStartEvent) {
    const id = String(event.active.id);
    if (id.startsWith("card:")) setActiveId(id.slice("card:".length));
  }

  function handleDragEnd(event: DragEndEvent) {
    setActiveId(null);
    if (!event.over) return;
    const activeIdStr = String(event.active.id);
    const overIdStr = String(event.over.id);
    if (!activeIdStr.startsWith("card:") || !overIdStr.startsWith("column:")) {
      return;
    }
    const dealId = activeIdStr.slice("card:".length);
    const targetPipelineId = overIdStr.slice("column:".length);
    const targetPipeline = pipelines.find((p) => p.id === targetPipelineId);
    const sourcePipeline = pipelineFor(dealId);
    if (!targetPipeline || !sourcePipeline) return;
    if (sourcePipeline.id === targetPipeline.id) return;

    // A Closed-Won drop awaits a live feed check; nothing here depends on the
    // outcome, and the gate reports its own refusals.
    void requestMove(dealId, sourcePipeline, targetPipeline);
  }

  const activeRow =
    activeId != null
      ? (savedProperties as unknown as CardRow[]).find((p) => p.id === activeId)
      : null;

  const closedWonDeal: ClosedWonDeal | null = useMemo(() => {
    if (!closedWonTarget) return null;
    const row = (savedProperties as unknown as CardRow[]).find(
      (p) => p.id === closedWonTarget.dealId,
    );
    if (!row) return null;
    const card = toCardData(row);
    return {
      id: row.id,
      address: card.addr1 ?? "this property",
      strategy: row.strategy ?? null,
      purchasePrice: row.purchase_price ?? null,
    };
  }, [closedWonTarget, savedProperties]);

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
    >
      <div className="flex min-h-0 flex-1 gap-3 overflow-x-auto pb-4">
        {pipelines.map((pipeline) => {
          const deals = getPropertiesByPipeline(
            pipeline,
          ) as unknown as CardRow[];
          return (
            <KanbanColumn
              key={pipeline.id}
              pipeline={pipeline}
              count={deals.length}
              onAddDeal={onAddDeal}
            >
              {deals.map((deal) => (
                // Cards in the Closed-Won column render greyed-out to mark the
                // deal as closed, but stay draggable so a mis-drop can be fixed.
                <KanbanCard
                  key={deal.id}
                  card={toCardData(deal)}
                  isClosedWon={pipeline.kind === "closed_won"}
                  isCheckingStatus={checkingDealId === deal.id}
                  onClick={onOpenDeal}
                  onRemove={requestRemove}
                />
              ))}
            </KanbanColumn>
          );
        })}
      </div>

      {/* Drag overlay — render the active card visually while dragging.
          dropAnimation=null skips dnd-kit's default 250ms flyback animation:
          the optimistic cache write has already moved the card to the target
          column, so animating the overlay back to source position is just
          a perceived delay. */}
      <DragOverlay dropAnimation={null}>
        {activeRow ? <KanbanCard card={toCardData(activeRow)} /> : null}
      </DragOverlay>

      {/* Closed-Won acquisition modal — intercepts moves into the Closed-Won
          stage to capture ownership / purchase / loan before the card moves. */}
      <ClosedWonDialog
        open={Boolean(closedWonTarget)}
        onOpenChange={(o) => {
          if (!o) setClosedWonTarget(null);
        }}
        deal={closedWonDeal}
        fromPipeline={closedWonTarget?.fromPipeline ?? null}
        toPipeline={closedWonTarget?.toPipeline ?? null}
      />

      {/* Remove confirmation. Deleting the row is permanent and takes its deal
          settings, checklists and stage history with it, so it's spelled out. */}
      <AlertDialog
        open={Boolean(removeTarget)}
        onOpenChange={(o) => {
          if (!o && !isRemoving) setRemoveTarget(null);
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove this deal?</AlertDialogTitle>
            <AlertDialogDescription>
              {removeTarget?.address} sold to another buyer, so it can't be
              acquired. Removing it deletes the deal and everything attached —
              your deal settings, checklists, notes and stage history. Manual
              accounting transactions are kept but lose their link to this
              property. This can't be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isRemoving}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => {
                // Keep the dialog open until the delete resolves, so the user
                // isn't left guessing whether it worked.
                e.preventDefault();
                void confirmRemove();
              }}
              disabled={isRemoving}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {isRemoving ? "Removing…" : "Remove deal"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </DndContext>
  );
}
